import type { ApiEnvelope, ApiErrorEnvelope } from '@qingxier/contracts';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  authenticated?: boolean;
}

export interface Transport {
  request(url: string, options: RequestOptions): Promise<{ status: number; json: any }>;
}

const fetchTransport: Transport = {
  async request(url, options) {
    const response = await fetch(url, {
      method: options.method ?? 'GET',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
    return { status: response.status, json: await response.json() };
  },
};

let transport: Transport = fetchTransport;
export const setTransport = (next: Transport) => { transport = next; };

const TOKEN_KEY = 'qingxier_admin_token';
const USERNAME_KEY = 'qingxier_admin_username';
const DISPLAY_NAME_KEY = 'qingxier_admin_display_name';
const REMEMBERED_USERNAME_KEY = 'qingxier_admin_remembered_username';

export const adminSession = {
  token: () => localStorage.getItem(TOKEN_KEY) ?? '',
  username: () => localStorage.getItem(USERNAME_KEY) ?? '',
  displayName: () => localStorage.getItem(DISPLAY_NAME_KEY) ?? '',
  rememberedUsername: () => localStorage.getItem(REMEMBERED_USERNAME_KEY) ?? '',
  save(token: string, admin: { username: string; displayName: string }) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USERNAME_KEY, admin.username);
    localStorage.setItem(DISPLAY_NAME_KEY, admin.displayName);
  },
  rememberUsername(username: string, remember: boolean) {
    if (remember) localStorage.setItem(REMEMBERED_USERNAME_KEY, username);
    else localStorage.removeItem(REMEMBERED_USERNAME_KEY);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(DISPLAY_NAME_KEY);
  },
};

export class AdminHttpClient {
  constructor(private readonly base = import.meta.env.VITE_API_BASE_URL || '') {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const token = adminSession.token();
    const authenticated = options.authenticated !== false;
    const response = await transport.request(`${this.base}${path}`, {
      ...options,
      headers: {
        ...(authenticated && token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Request-Id': `admin_${Date.now()}`,
        ...options.headers,
      },
    });
    const body = response.json as ApiEnvelope<T> | ApiErrorEnvelope;
    if (response.status < 200 || response.status >= 300 || body.code !== 0) {
      if (response.status === 401 && authenticated) adminSession.clear();
      const error = new Error(body.message || '请求失败') as Error & {
        code?: number;
        error?: string;
        traceId?: string;
      };
      error.code = body.code;
      error.error = 'error' in body ? body.error : undefined;
      error.traceId = body.traceId;
      throw error;
    }
    return (body as ApiEnvelope<T>).data;
  }

  async upload<T>(path: string, file: Blob, filename: string): Promise<T> {
    const token = adminSession.token();
    const form = new FormData();
    form.append('file', file, filename);
    const response = await fetch(`${this.base}${path}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-Request-Id': `admin_${Date.now()}`,
      },
      body: form,
    });
    let body: any;
    try {
      body = await response.json();
    } catch {
      throw new Error('上传失败');
    }
    if (response.status < 200 || response.status >= 300 || body.code !== 0) {
      throw new Error(body.message || '上传失败');
    }
    return (body as ApiEnvelope<T>).data;
  }
}

export const adminHttp = new AdminHttpClient();
